import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import moment from "moment";
import MainModal from "components/MainModal";
import { MainTable, TableMenu } from "components";
import { IOption } from "components/TableMenu/TableMenuInterfaces";
import { IMainTableColumn } from "components/MainTable/MainTableInterfaces";
import { FeatureToggleConfirmModal } from "pages/Osorno/components";
import { ThirdPartyApiAccessEntity } from "entities/ThirdPartyApiAccess/ThirdPartyApiAccessEntity";
import { useStyles } from "./ApiAccessOversight.styles";
import { ApiKeyStatusEnum, IApiAccessCorporation, IApiAccessKey } from "./ApiAccessOversightInterfaces";
import { deriveKeyStatus } from "./apiAccessUtils";
import useApiAccessToggle from "./hooks/useApiAccessToggle";

interface IApiAccessKeysModalProps {
    // When set, the modal is open and shows this Corporation's keys; undefined = closed.
    corporation: IApiAccessCorporation | undefined;
    onClose: () => void;
}

interface IApiAccessKeyColumns extends Omit<IMainTableColumn, "render"> {
    render: (row: IApiAccessKey) => string | JSX.Element | number;
}

/**
 * Per-key drill-down for one Corporation, shown as an MBO modal (MainModal) over the
 * API Access list. The keys query skips while closed (corporate_id undefined). Per-key
 * Revoke uses the FeatureToggleConfirmModal, stacked on top of this modal.
 */
export const ApiAccessKeysModal = ({ corporation, onClose }: IApiAccessKeysModalProps) => {
    const classes = useStyles();
    const { t } = useTranslation();
    const { loading, keys, apolloQueryError, refetch } = ThirdPartyApiAccessEntity.GetApiAccessKeys(
        corporation?.corporate_id,
    );
    const { revokeKey } = useApiAccessToggle();
    const [revokeTarget, setRevokeTarget] = useState<IApiAccessKey | null>(null);
    const [revokeError, setRevokeError] = useState<boolean>(false);

    const renderStatus = (key: IApiAccessKey) => {
        const status = deriveKeyStatus(key);
        if (status === ApiKeyStatusEnum.Revoked) {
            return <span className={classes.statusRevoked}>{t("api_access.statuses.revoked")}</span>;
        }
        if (status === ApiKeyStatusEnum.Expired) {
            return <span className={classes.statusExpired}>{t("api_access.statuses.expired")}</span>;
        }
        return <span className={classes.statusButton}>{t("api_access.statuses.active")}</span>;
    };

    const columns = useMemo(
        (): IApiAccessKeyColumns[] => [
            {
                id: "label",
                label: t("api_access.key_table_headers.label"),
                hideSortIcon: false,
                render: (key) => key.label || key.key_prefix,
            },
            {
                id: "key_prefix",
                label: t("api_access.key_table_headers.key_prefix"),
                hideSortIcon: true,
                render: (key) => `${key.key_prefix}…`,
            },
            {
                id: "created_at",
                label: t("api_access.key_table_headers.created_at"),
                hideSortIcon: true,
                render: (key) => moment(key.created_at).format("YYYY/MM/DD"),
            },
            {
                id: "expires_at",
                label: t("api_access.key_table_headers.expires_at"),
                hideSortIcon: true,
                render: (key) => moment(key.expires_at).format("YYYY/MM/DD"),
            },
            {
                id: "last_used_at",
                label: t("api_access.key_table_headers.last_used_at"),
                hideSortIcon: true,
                render: (key) =>
                    key.last_used_at ? moment(key.last_used_at).format("YYYY/MM/DD HH:mm") : t("api_access.never"),
            },
            {
                id: "status",
                label: t("api_access.key_table_headers.status"),
                hideSortIcon: true,
                render: (key) => renderStatus(key),
            },
            {
                id: "options",
                label: "",
                hideSortIcon: true,
                render: (key) => {
                    const isActive = deriveKeyStatus(key) === ApiKeyStatusEnum.Active;
                    const options: IOption[] = [
                        {
                            name: t("api_access.revoke"),
                            callback: () => setRevokeTarget(key),
                            disabled: !isActive,
                            disabledTooltip: t("api_access.revoke_disabled_tooltip"),
                            customStyles: { fontWeight: 600 },
                        },
                    ];
                    return <TableMenu options={options} />;
                },
            },
        ],
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [t, classes.statusButton, classes.statusRevoked, classes.statusExpired],
    );

    const handleRevokeConfirm = async () => {
        if (!revokeTarget) {
            return;
        }
        try {
            setRevokeError(false);
            await revokeKey(revokeTarget);
            setRevokeTarget(null);
            await refetch();
        } catch {
            // Close the confirm modal so the inline error on the keys modal is visible; without
            // this the confirm modal stays open with no feedback on a failed revoke.
            setRevokeTarget(null);
            setRevokeError(true);
        }
    };

    return (
        <>
            <MainModal
                isOpen={Boolean(corporation)}
                onClose={onClose}
                showCloseButton
                showFooter={false}
                title={corporation?.name ?? ""}
                customModalStyles={{ width: "900px" }}
                contentClassName={classes.modalContent}
            >
                <p className={classes.modalSubtitle}>{t("api_access.key_detail_subtitle")}</p>
                {apolloQueryError && <p className={classes.errorText}>{t("api_access.load_error")}</p>}
                {revokeError && <p className={classes.errorText}>{t("api_access.revoke_error")}</p>}
                <MainTable
                    columns={columns as unknown as IMainTableColumn[]}
                    rows={!loading && !apolloQueryError ? keys : []}
                    loading={loading}
                    pagination
                />
            </MainModal>
            <FeatureToggleConfirmModal
                open={Boolean(revokeTarget)}
                onClose={() => setRevokeTarget(null)}
                onConfirm={handleRevokeConfirm}
                title={t("api_access.revoke_modal.title")}
                description={t("api_access.revoke_modal.description")}
                confirmLabel={t("api_access.revoke_modal.confirm")}
                confirmButtonColor="error"
            />
        </>
    );
};
